/**
 * Facilities, Entrega 3a (ADR 0036): ordens de serviço (OS), checklists versionados, horas,
 * materiais, evidências, ocorrências e avaliação de prestadores. Custos só informativos (D2).
 */
import { z } from 'zod';
import {
  defineFormSchemas,
  formFieldVisible,
  validateFormFieldValues,
  type FormSchemaDef,
} from './forms.ts';
import { FACILITIES_IMPACTS, FACILITIES_PRIORITIES } from './facilities.ts';
import { localizedText } from './schemas.ts';

export const WORK_ORDER_STATUSES = [
  'open',
  'planned',
  'in_progress',
  'on_hold',
  'completed',
  'accepted',
  'cancelled',
] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];
/** Situações em que a OS não muda mais. */
export const WORK_ORDER_FINAL: readonly WorkOrderStatus[] = ['accepted', 'cancelled'];
/** Situações que contam como OS encerrada para o chamado poder concluir a execução. */
export const WORK_ORDER_CLOSED: readonly WorkOrderStatus[] = ['completed', 'accepted', 'cancelled'];
/** Situações em que horas, materiais e respostas do checklist podem ser lançados. */
export const WORK_ORDER_EXECUTING: readonly WorkOrderStatus[] = ['in_progress', 'on_hold'];

/** Tipos de OS. A preventiva chegou na Entrega 3b, com os planos. */
export const WORK_ORDER_KINDS = ['corrective', 'inspection', 'improvement', 'preventive'] as const;
export const WORK_ORDER_ORIGINS = ['request', 'team', 'occurrence', 'plan'] as const;
export const WORK_ORDER_EXECUTORS = ['internal', 'provider'] as const;

/** Ações e as situações de onde cada uma parte (o banco confere de novo). */
export const WORK_ORDER_TRANSITIONS = {
  plan: { from: ['open', 'planned'], to: 'planned' },
  start: { from: ['planned'], to: 'in_progress' },
  hold: { from: ['in_progress'], to: 'on_hold' },
  resume: { from: ['on_hold'], to: 'in_progress' },
  complete: { from: ['in_progress'], to: 'completed' },
  accept: { from: ['completed'], to: 'accepted' },
  reject: { from: ['completed'], to: 'in_progress' },
  cancel: { from: ['open', 'planned', 'in_progress', 'on_hold'], to: 'cancelled' },
} as const satisfies Record<string, { from: readonly WorkOrderStatus[]; to: WorkOrderStatus }>;
export type WorkOrderAction = keyof typeof WORK_ORDER_TRANSITIONS;

export const WORK_ORDER_ATTACHMENT_CATEGORIES = [
  'photo_before',
  'photo_after',
  'report',
  'checklist',
  'receipt',
  'other',
] as const;
/** Evidências mínimas por tipo de OS na Entrega 3a (configuráveis na 3c). */
export const WORK_ORDER_REQUIRED_EVIDENCE: Record<
  (typeof WORK_ORDER_KINDS)[number],
  readonly (typeof WORK_ORDER_ATTACHMENT_CATEGORIES)[number][]
> = {
  corrective: ['photo_after'],
  inspection: [],
  improvement: [],
  preventive: [],
};

export const OCCURRENCE_KINDS = ['delay', 'non_conformity', 'damage', 'safety', 'other'] as const;
export const OCCURRENCE_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export const RATING_CRITERIA = ['punctuality', 'quality', 'safety', 'tidiness'] as const;

// ----- Checklists (mecanismo compartilhado de formulários) -----

/**
 * Tipos de item do checklist. "conformity" é conforme / não conforme / não se aplica; o número
 * pode ter limites (fora deles é não conformidade); "attachment" é uma evidência anexada.
 */
export const CHECKLIST_FIELD_TYPES = [
  'conformity',
  'number',
  'text',
  'long_text',
  'date',
  'select',
  'checkbox',
  'attachment',
] as const;
export const CONFORMITY_VALUES = ['ok', 'nok', 'na'] as const;
const checklistForms = defineFormSchemas(CHECKLIST_FIELD_TYPES);
export const checklistSchemaInput = checklistForms.schema;
export type ChecklistSchema = z.infer<typeof checklistSchemaInput>;

/** Respostas do checklist: o valor de cada item e a observação (exigida na não conformidade). */
export interface ChecklistAnswers {
  values: Record<string, unknown>;
  notes: Record<string, string>;
}

/** Itens não conformes: "não conforme" ou número fora dos limites do item. */
export function checklistNonConformities(schema: FormSchemaDef, answers: ChecklistAnswers) {
  const out: { key: string; reason: 'nok' | 'out_of_range' }[] = [];
  for (const f of schema.fields) {
    if (!formFieldVisible(f, answers.values)) continue;
    const v = answers.values[f.key];
    if (f.type === 'conformity' && v === 'nok') out.push({ key: f.key, reason: 'nok' });
    if (f.type === 'number' && typeof v === 'number') {
      if ((f.min !== undefined && v < f.min) || (f.max !== undefined && v > f.max)) {
        out.push({ key: f.key, reason: 'out_of_range' });
      }
    }
  }
  return out;
}

/**
 * Confere as respostas. Na conclusão (`complete`), os itens obrigatórios precisam de resposta e
 * cada não conformidade precisa de observação; antes disso, só tipos e opções. Evidências
 * (itens "attachment") são conferidas nos anexos da OS.
 */
export function validateChecklistAnswers(
  schema: FormSchemaDef,
  answers: ChecklistAnswers,
  complete: boolean,
) {
  let issues = validateFormFieldValues(schema, answers.values);
  if (!complete) issues = issues.filter((i) => i.message !== 'Obrigatório');
  const keys = new Set(schema.fields.map((f) => f.key));
  for (const key of Object.keys(answers.notes)) {
    if (!keys.has(key)) issues.push({ path: key, message: 'Item que não existe no checklist' });
  }
  if (complete) {
    for (const nc of checklistNonConformities(schema, answers)) {
      if (!answers.notes[nc.key]?.trim()) {
        issues.push({ path: nc.key, message: 'Descreva a não conformidade' });
      }
    }
  }
  return issues;
}

// ----- Entradas -----

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const optionalId = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const optionalDateTime = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/)
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const decimal = (scale: number) =>
  z
    .string()
    .trim()
    .regex(new RegExp(`^-?\\d{1,15}(\\.\\d{1,${scale}})?$`), 'Use números, com ponto nos decimais');
const currency = z.string().regex(/^[A-Z]{3}$/, 'Moeda inválida');
const money = z
  .object({ amount: decimal(4), currency })
  .refine((m) => !m.amount.startsWith('-'), { path: ['amount'], message: 'Valor negativo' })
  .nullable()
  .optional();

/** Dados da OS que a equipe mantém enquanto ela está aberta ou planejada. */
export const workOrderDataInput = z.object({
  branchId: z.uuid(),
  locationId: optionalId,
  assetId: optionalId,
  kind: z.enum(WORK_ORDER_KINDS),
  title: z.string().trim().min(1).max(200),
  description: optional(4000),
  priority: z.enum(FACILITIES_PRIORITIES).default('normal'),
  impact: z.enum(FACILITIES_IMPACTS).default('low'),
  safetyRisk: z.boolean().default(false),
  costCenterId: optionalId,
  departmentId: optionalId,
  takeOutOfService: z.boolean().default(false),
});
export const workOrderCreateInput = workOrderDataInput.extend({ legalEntityId: z.uuid() });

/** "Gerar OS" a partir do chamado: o resto vem preenchido do chamado. */
export const workOrderFromRequestInput = z.object({
  kind: z.enum(WORK_ORDER_KINDS),
  title: z.string().trim().min(1).max(200).optional(),
  takeOutOfService: z.boolean().default(false),
});

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');

export const workOrderPlanInput = z
  .object({
    executorType: z.enum(WORK_ORDER_EXECUTORS),
    internalOwnerUserId: z.uuid(),
    technicianUserId: optionalId,
    providerPartnerId: optionalId,
    providerTechnicianName: optional(150),
    plannedStart: isoDate,
    plannedEnd: isoDate,
    estimated: money,
    checklistTemplateId: optionalId,
    purchaseRequestId: optionalId,
  })
  .refine((v) => v.executorType !== 'provider' || !!v.providerPartnerId, {
    path: ['providerPartnerId'],
    message: 'Escolha o prestador no cadastro de parceiros',
  })
  .refine((v) => v.executorType !== 'internal' || !!v.technicianUserId, {
    path: ['technicianUserId'],
    message: 'Escolha o técnico executor',
  })
  .refine((v) => v.plannedEnd.slice(0, 10) >= v.plannedStart.slice(0, 10), {
    path: ['plannedEnd'],
    message: 'O fim previsto não pode ser antes do início',
  });

export const workOrderReasonInput = z.object({ reason: z.string().trim().min(5).max(2000) });

/** Compra pedida a partir da OS (Entrega 5): vínculo "depende de" com a solicitação de Compras. */
export const workOrderPurchaseInput = z.object({ purchaseRequestId: z.uuid() });

/** O que a OS suspensa espera (Entrega 5); o SLA pausa só se a política da atividade permitir. */
export const WORK_ORDER_HOLD_CATEGORIES = [
  'purchase',
  'material',
  'provider',
  'site_access',
  'other',
] as const;
export type WorkOrderHoldCategory = (typeof WORK_ORDER_HOLD_CATEGORIES)[number];

/**
 * Categoria de pausa do SLA corporativo para cada motivo (padrão provisório): compra é outra área
 * (Compras); material e prestador são terceiros; acesso ao local depende de quem pediu; "outro"
 * não pausa.
 */
export const WORK_ORDER_HOLD_SLA_CATEGORY: Record<WorkOrderHoldCategory, string | null> = {
  purchase: 'other_area',
  material: 'third_party',
  provider: 'third_party',
  site_access: 'requester',
  other: null,
};

export const workOrderHoldInput = z.object({
  category: z.enum(WORK_ORDER_HOLD_CATEGORIES),
  reason: z.string().trim().min(5).max(2000),
});

export const workOrderCompleteInput = z.object({
  resolution: z.string().trim().min(5).max(4000),
  finishedAt: optionalDateTime,
  service: money,
});

export const checklistAnswersInput = z.object({
  values: z.record(z.string(), z.unknown()).default({}),
  notes: z.record(z.string(), z.string().trim().max(2000)).default({}),
});

export const laborInput = z
  .object({
    userId: optionalId,
    providerTechnician: optional(150),
    workDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    hours: decimal(2),
    note: optional(1000),
    reversesId: optionalId,
  })
  .refine((v) => !!v.userId !== !!v.providerTechnician, {
    path: ['userId'],
    message: 'Informe a pessoa da equipe ou o técnico do prestador',
  })
  .refine(
    (v) => {
      const h = Number(v.hours);
      return v.reversesId ? h < 0 && h >= -24 : h >= 0.25 && h <= 24;
    },
    { path: ['hours'], message: 'De 0,25 a 24 horas (estorno: negativo)' },
  );

export const materialInput = z
  .object({
    description: z.string().trim().min(1).max(300),
    quantity: decimal(3),
    unit: z.string().trim().min(1).max(20),
    unitCost: money,
    reversesId: optionalId,
    // Item do estoque de Facilities (Entrega 4b): o lançamento baixa o saldo do estoque escolhido.
    stockItemId: optionalId,
    stockLocationId: optionalId,
  })
  .refine(
    (v) => {
      const q = Number(v.quantity);
      return v.reversesId ? q < 0 : q > 0;
    },
    { path: ['quantity'], message: 'Quantidade positiva (estorno: negativa)' },
  )
  .refine((v) => !!v.reversesId || !v.stockItemId === !v.stockLocationId, {
    path: ['stockLocationId'],
    message: 'Escolha o estoque de onde sai o item',
  });

export const occurrenceInput = z.object({
  kind: z.enum(OCCURRENCE_KINDS),
  severity: z.enum(OCCURRENCE_SEVERITIES).default('medium'),
  description: z.string().trim().min(5).max(2000),
  partnerId: optionalId,
});
export const occurrenceCloseInput = z.object({
  correctiveAction: z.string().trim().min(5).max(2000),
});
/** Desdobramento autorizado: nova OS a partir de uma ocorrência, com justificativa. */
export const occurrenceFollowUpInput = z.object({
  title: z.string().trim().min(1).max(200),
  justification: z.string().trim().min(5).max(2000),
});

export const ratingInput = z.object({
  punctuality: z.number().int().min(1).max(5),
  quality: z.number().int().min(1).max(5),
  safety: z.number().int().min(1).max(5),
  tidiness: z.number().int().min(1).max(5),
  comment: optional(1000),
});

export const checklistTemplateInput = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_.-]{0,29}$/, 'Use letras, números, ponto, hífen ou _'),
  name: localizedText,
  description: z
    .object({ 'pt-BR': z.string().trim().max(1000), en: z.string().trim().max(1000) })
    .default({ 'pt-BR': '', en: '' }),
  suggestedAssetClassId: optionalId,
  isActive: z.boolean().default(true),
});

/** Soma por moeda (sem conversão): valores em texto decimal com 4 casas. */
export function sumByCurrency(items: { amount: string; currency: string }[]) {
  const totals = new Map<string, bigint>();
  for (const i of items) {
    const neg = i.amount.startsWith('-');
    const [int, dec = ''] = i.amount.replace('-', '').split('.');
    const cents = BigInt(int!) * 10000n + BigInt((dec + '0000').slice(0, 4));
    totals.set(i.currency, (totals.get(i.currency) ?? 0n) + (neg ? -cents : cents));
  }
  return [...totals.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, v]) => {
      const neg = v < 0n;
      const abs = neg ? -v : v;
      const amount = `${neg ? '-' : ''}${abs / 10000n}.${(abs % 10000n).toString().padStart(4, '0')}`;
      return { currency, amount };
    });
}
